import { AlertOctagon, Maximize2, Minimize2, MoveRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError } from "../../shared/api/client";
import { Button, InlineAlert, StatusBadge, cn } from "../../shared/ui";
import type {
  TransitionView,
  WorkItemStatus,
  WorkItemSummary,
} from "../../shared/types/api";
import { WorkItemStatusDrawer } from "../workitems/WorkItemStatusDrawer";
import { WorkItemTypeBadge } from "../workitems/WorkItemTypeBadge";
import {
  PRIORITY_TONE,
  SEVERITY_LABELS,
  SEVERITY_TONE,
  STATUS_LABELS,
  STATUS_ORDER,
} from "../workitems/workItemVocabulary";

/**
 * §51's default board, derived from the one list of statuses rather than restating it.
 *
 * <p>It used to be its own copy, and the copy went stale: when development done became a status
 * of its own and code review stopped being one, this list was not the place anybody thought to
 * change. A card in the new status then matched no column and simply vanished from the board —
 * still in the sprint, still in every total, and nowhere a person could see it.
 *
 * <p>Cancelled is the one deliberate omission. It is a state work leaves the board in, not a
 * place work sits, and a column of abandoned cards on a sprint board is noise every standup has
 * to look past. {@link OFF_BOARD} keeps that omission honest, and a test holds it to it.
 */
const COLUMNS: WorkItemStatus[] = STATUS_ORDER.filter((status) => status !== "CANCELLED");

/** Statuses deliberately left off the board, for the test that no other status is. */
export const OFF_BOARD: WorkItemStatus[] = ["CANCELLED"];

export { COLUMNS as BOARD_COLUMNS };

/**
 * The sprint board (§51, §52).
 *
 * <p>Dragging changes status, and every change writes history — which is the point §51 makes when
 * it says so explicitly. A board that only moved cards would answer "where is this now" and
 * nothing about how it got there.
 *
 * <p>Each card also carries a Move button. Drag-and-drop is the fast path and it is unreachable
 * by keyboard; a board where work can only be moved with a mouse excludes people for no reason.
 * It opens the same drawer the item's own page uses, so the workflow is enforced and explained in
 * one place rather than two.
 *
 * <p>A drop names a destination, not an act, so the board asks the server which act that would be.
 * Dropping somewhere the workflow does not allow is refused with what is actually possible, and a
 * drop that goes backwards opens the drawer for its reason rather than moving silently.
 *
 * <p>Cancelled items are deliberately absent. They are not a column anybody works through, and a
 * board with a graveyard on the end makes the seven live columns narrower for no benefit.
 */
export function SprintBoard({
  items,
  canEdit,
  onChanged,
  title,
}: {
  items: WorkItemSummary[];
  canEdit: boolean;
  onChanged: () => void;
  /** Named in the full-screen bar, where the page heading behind it is no longer visible. */
  title?: string;
}) {
  const navigate = useNavigate();
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<WorkItemStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [moving, setMoving] = useState<{ item: WorkItemSummary; step?: TransitionView } | null>(
    null,
  );
  const [maximised, setMaximised] = useState(false);

  /* Seven columns inside a card inside a page with a sidebar leaves each one about a card wide,
     so the board is read three columns at a time. Full screen is the difference between seeing
     where the work is and scrolling to find out. */
  useEffect(() => {
    if (!maximised) {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      // Escape closes the innermost thing. With a drawer open it belongs to the drawer, and
      // handling it here as well would dismiss both at once for one keypress.
      if (event.key === "Escape" && moving == null) {
        setMaximised(false);
      }
    };
    /*
     * Escape is what everybody tries first, and without it the only way out is a button that has
     * scrolled off the side of a horizontally scrolling board.
     *
     * Capture phase, deliberately. The drawer listens on `document`, which fires before `window`
     * in the bubble phase; React then flushes its close synchronously, this effect re-runs, and
     * the event reaches window to find a freshly registered listener whose `moving` is already
     * null — so one keypress closed the drawer and exited full screen. Capturing at window runs
     * this first, against the state as it was when the key was pressed.
     */
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [maximised, moving]);

  /*
   * The scroll lock is its own effect, keyed only on `maximised`.
   *
   * Sharing the one above meant re-running whenever a drawer opened: the cleanup restored the
   * original overflow, then the re-run captured whatever the drawer had just set. Exiting full
   * screen then "restored" hidden and left the page unable to scroll. Two owners of one global
   * have to take it in a strict order, so this one captures once on the way in and puts back
   * exactly what it found on the way out.
   */
  useEffect(() => {
    if (!maximised) {
      return;
    }
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [maximised]);

  const move = async (item: WorkItemSummary, status: WorkItemStatus) => {
    setError(null);
    try {
      const available = await api.get<TransitionView[]>(
        `/api/v1/work-items/${item.id}/transitions`,
      );
      const step = available.find((candidate) => candidate.to === status);

      if (!step) {
        setError(
          `${item.itemKey} cannot go straight to ${STATUS_LABELS[status]}. From `
          + `${STATUS_LABELS[item.status]} you can: `
          + `${available.map((candidate) => candidate.label).join(", ")}.`,
        );
        return;
      }

      if (step.requiresReason) {
        setMoving({ item, step });
        return;
      }

      await api.post(`/api/v1/work-items/${item.id}/transitions`, {
        transition: step.transition,
        reason: null,
        reasonComment: null,
      });
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not move that item.");
    }
  };

  const drop = (status: WorkItemStatus) => {
    const id = dragging;
    setDragging(null);
    setOver(null);
    if (!id) {
      return;
    }
    const item = items.find((candidate) => candidate.id === id);
    if (!item || item.status === status) {
      return;
    }
    void move(item, status);
  };

  const columns = (
    <div
      className={cn(
        "flex gap-3 overflow-x-auto pb-2",
        // min-h-0 is load-bearing: without it a flex child refuses to shrink below its content
        // and the columns push the board off the bottom of the screen instead of scrolling.
        maximised && "min-h-0 flex-1 px-4",
      )}
    >
      {COLUMNS.map((status) => {
          const inColumn = items.filter((item) => item.status === status);
          const points = inColumn.reduce(
            (total, item) => total + (item.storyPoints ?? 0),
            0,
          );

          return (
            <section
              key={status}
              onDragOver={(event) => {
                if (canEdit) {
                  event.preventDefault();
                  setOver(status);
                }
              }}
              onDragLeave={() => setOver((current) => (current === status ? null : current))}
              onDrop={() => drop(status)}
              className={cn(
                "flex flex-col rounded-xl border bg-ink-50/50 transition-colors",
                // Inside the card there is not room for seven, so they keep a readable width and
                // the board scrolls. Maximised, they share the screen instead: the whole point of
                // asking for more room is to stop scrolling to find out where the work is. The
                // minimum keeps a card legible, so a narrow screen still scrolls rather than
                // squeezing seven columns into slivers.
                maximised ? "min-w-52 max-h-full flex-1" : "w-64 shrink-0",
                over === status
                  ? "border-brand-400 bg-brand-50"
                  : "border-ink-200",
              )}
            >
              <header className="flex items-baseline justify-between gap-2 border-b border-ink-200 px-3 py-2">
                <h3 className="text-sm font-medium text-ink-800">
                  {STATUS_LABELS[status]}
                </h3>
                <span className="tnum text-xs text-ink-500">
                  {inColumn.length}
                  {points > 0 && ` · ${points}pt`}
                </span>
              </header>

              <div className={cn("flex-1 space-y-2 p-2", maximised && "overflow-y-auto")}>
                {inColumn.length === 0 ? (
                  <p className="px-1 py-3 text-center text-xs text-ink-400">Nothing here</p>
                ) : (
                  inColumn.map((item) => (
                    <article
                      key={item.id}
                      draggable={canEdit}
                      onDragStart={() => setDragging(item.id)}
                      onDragEnd={() => {
                        setDragging(null);
                        setOver(null);
                      }}
                      className={cn(
                        "rounded-lg border border-ink-200 bg-surface p-2.5 shadow-sm transition-opacity",
                        canEdit && "cursor-grab active:cursor-grabbing",
                        dragging === item.id && "opacity-40",
                      )}
                    >
                      <div className="flex items-center gap-1.5">
                        <WorkItemTypeBadge type={item.workItemType} />
                        <button
                          type="button"
                          onClick={() => navigate(`/work-items/${item.itemKey}`)}
                          className="font-mono text-[11px] text-brand-600 hover:underline"
                        >
                          {item.itemKey}
                        </button>
                        {item.openBlockerCount > 0 && (
                          <span
                            className="ml-auto flex items-center gap-0.5 text-red-600"
                            title={`${item.openBlockerCount} open blocker${item.openBlockerCount === 1 ? "" : "s"}`}
                          >
                            <AlertOctagon className="h-3 w-3" />
                          </span>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => navigate(`/work-items/${item.itemKey}`)}
                        className="mt-1 block w-full text-left text-sm text-ink-900"
                      >
                        {item.title}
                      </button>

                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        {item.severity ? (
                          <StatusBadge tone={SEVERITY_TONE[item.severity]}>
                            {SEVERITY_LABELS[item.severity]}
                          </StatusBadge>
                        ) : (
                          <StatusBadge tone={PRIORITY_TONE[item.priority]}>
                            {item.priority}
                          </StatusBadge>
                        )}
                        {item.storyPoints != null && (
                          <span className="tnum rounded bg-ink-100 px-1.5 py-0.5 text-[11px] text-ink-600">
                            {item.storyPoints}
                          </span>
                        )}
                        {/* §10: what this card is shipping in, when it is promised to something. */}
                        {item.releaseVersion && (
                          <span
                            className="truncate rounded bg-brand-50 px-1.5 py-0.5 text-[11px] text-brand-700"
                            title={`Shipping in ${item.releaseVersion}`}
                          >
                            {item.releaseVersion}
                          </span>
                        )}
                        <span className="ml-auto truncate text-[11px] text-ink-500">
                          {item.assigneeName ?? "Unassigned"}
                        </span>
                      </div>

                      {canEdit && (
                        // The keyboard-reachable path. Dragging is faster; this is the one that
                        // works without a mouse, and it offers only the legal moves.
                        <Button
                          variant="secondary"
                          size="sm"
                          className="mt-2 h-7 w-full text-[11px]"
                          icon={<MoveRight className="h-3.5 w-3.5" />}
                          aria-label={`Move ${item.itemKey}`}
                          onClick={() => setMoving({ item })}
                        >
                          Move
                        </Button>
                      )}
                    </article>
                  ))
                )}
              </div>
            </section>
          );
        })}
    </div>
  );

  const maximiseButton = (
    <Button
      variant="ghost"
      size="sm"
      icon={maximised ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
      onClick={() => setMaximised(!maximised)}
    >
      {maximised ? "Exit full screen" : "Maximise"}
    </Button>
  );

  const drawer = (
    <WorkItemStatusDrawer
      item={moving?.item ?? null}
      preset={moving?.step ?? null}
      onClose={() => setMoving(null)}
      onSaved={onChanged}
    />
  );

  if (maximised) {
    return (
      /* z-40, below the drawer's z-50, so moving a card still works from here rather than
         opening a drawer nobody can see. */
      <div className="fixed inset-0 z-40 flex flex-col bg-surface">
        <header className="flex items-center justify-between gap-3 border-b border-ink-200 px-4 py-3">
          <h2 className="truncate text-sm font-semibold text-ink-900">{title ?? "Board"}</h2>
          <div className="flex items-center gap-2">
            <span className="hidden text-xs text-ink-500 sm:inline">Esc to exit</span>
            {maximiseButton}
          </div>
        </header>

        {error && (
          <div className="px-4 pt-3">
            <InlineAlert tone="error">{error}</InlineAlert>
          </div>
        )}

        {columns}
        {drawer}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {error && <InlineAlert tone="error">{error}</InlineAlert>}

      <div className="flex justify-end">{maximiseButton}</div>

      {columns}
      {drawer}
    </div>
  );
}
